/**
 * Card Component
 *
 * A container component with optional header, body, and footer sections.
 */

import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/cn';

// ============================================
// Card Variants
// ============================================

const cardVariants = cva(
  [
    'rounded-lg border',
    'bg-card dark:bg-card-dark',
    'border-border dark:border-border-dark',
  ],
  {
    variants: {
      shadow: {
        none: '',
        sm: 'shadow-sm',
        md: 'shadow-md',
        lg: 'shadow-lg',
      },
      hover: {
        none: '',
        lift: 'transition-shadow hover:shadow-card-hover',
        border: 'transition-colors hover:border-primary',
      },
    },
    defaultVariants: {
      shadow: 'sm',
      hover: 'none',
    },
  }
);

// ============================================
// Card Props
// ============================================

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

// ============================================
// Card Component
// ============================================

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, shadow, hover, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(cardVariants({ shadow, hover }), className)}
        {...props}
      />
    );
  }
);

Card.displayName = 'Card';

// ============================================
// Card Header
// ============================================

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Title for the card */
  title?: string;
  /** Description/subtitle */
  description?: string;
  /** Action element (button, etc.) */
  action?: React.ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, title, description, action, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-start justify-between p-6 pb-4', className)}
        {...props}
      >
        {(title || description) ? (
          <div className="space-y-1">
            {title && (
              <h3 className="text-lg font-semibold text-foreground dark:text-foreground-dark">
                {title}
              </h3>
            )}
            {description && (
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                {description}
              </p>
            )}
          </div>
        ) : (
          children
        )}
        {action && <div className="ml-4">{action}</div>}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

// ============================================
// Card Body
// ============================================

export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

export const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('p-6 pt-0', className)}
        {...props}
      />
    );
  }
);

CardBody.displayName = 'CardBody';

// ============================================
// Card Footer
// ============================================

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center p-6 pt-0',
          'border-t border-border dark:border-border-dark mt-4 pt-4',
          className
        )}
        {...props}
      />
    );
  }
);

CardFooter.displayName = 'CardFooter';
